import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { configureClient, isNotLoggedIn, login, deviceLogin } from './http';
import { setupInstance } from './instance';
import { AuthClient } from './types';

export * from './plugins/types';
export * from './types';

export interface PortalOptions {
  apiUrl: string;
  portalUrl: string;
  auth: AuthClient;
  plans: Array<string>;
  consent: boolean;
  portalPages: Record<string, React.FC>;
  landingPages: Record<string, React.FC>;
}

function getPath(url: string) {
  const el = document.createElement('a');
  el.href = url;
  return el.pathname;
}

function normalize(path: string) {
  const fragmentPos = path.indexOf('#');

  if (fragmentPos !== -1) {
    path = path.substring(0, fragmentPos);
  }

  const queryPos = path.indexOf('?');

  if (queryPos !== -1) {
    path = path.substring(0, queryPos);
  }

  if (path.endsWith('/')) {
    path = path.substring(0, path.length - 1);
  }

  return path;
}

function loadSdk(url: string) {
  return new Promise<void>((resolve, reject) => {
    const script = document.body.appendChild(document.createElement('script'));
    script.src = `${url}/api-docs/sdk.js`;
    script.onload = () => resolve();
    script.onerror = (event, _source, _lineno, _colno, error) => reject(error || event);
  });
}

export async function runPortal(options: PortalOptions) {
  await loadSdk(options.apiUrl);

  configureClient(options.auth, options.apiUrl);

  const clientKeyQuery = '?client_key=';
  const currentUrl = normalize(location.href);
  const authUrl = `${normalize(options.portalUrl)}/auth`;
  const loginUrl = `${normalize(options.portalUrl)}/login`;
  const deviceLoginUrl = `${normalize(options.portalUrl)}/devicelogin`;
  const instance = setupInstance(options.portalPages, {
    providers: options.auth.providers,
    plans: options.plans,
    consent: options.consent,
    apiUrl: options.apiUrl,
    basePath: normalize(getPath(options.portalUrl)),
  });

  if (currentUrl === deviceLoginUrl && location.search.startsWith(clientKeyQuery)) {
    deviceLogin(location.search.substring(clientKeyQuery.length));
  } else if (currentUrl === loginUrl && isNotLoggedIn()) {
    login();
  } else if (currentUrl !== authUrl) {
    const root = createRoot(document.querySelector('#app'));
    root.render(
      <App
        pages={{
          ...options.portalPages,
          ...options.landingPages,
        }}
        instance={instance}
        onChanged={options.auth.changed}
      />,
    );
  }

  return instance;
}
